import {
  AdditiveBlending,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  NormalBlending,
  Points,
  ShaderMaterial,
} from 'three';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import { Metrics } from '../_app/cuchillo/core/Metrics';

// RESPONSIVE: els settings ESPACIALS es guarden com a FRACCIÓ de pantalla i es multipliquen per
// Metrics.WIDTH / HEIGHT al punt d'ús (init/resize/build) → el 3D escala amb l'amplada igual que el CSS.
//   · SCALE (× WIDTH): mides, extensions i amplituds horitzontals (fracció = px_@1440 / 1440).
//   · SPEED/VERT (× HEIGHT): velocitats i marges verticals (fracció = px_@900 / 900).
//   · AREA (× WIDTH × HEIGHT): comptes → densitat constant (fracció = count_@1440x900 / (1440·900)).
//   · NONE: sense multiplicar (colors, opacitats, factors unitaris, freqüències temporals).
const MAX_COUNT = 4000; // sostre de seguretat (GPU) per al count escalat per àrea

// Bombolles d'oxigen (2n render). Cada partícula té X, Y, Z i cada eix es calcula diferent:
//   · Y = FLOTACIÓ (pujada acumulada, velocitat random per partícula) + DISPLACE de scroll (directe,
//         `scrollY × scrollStrength`, COMÚ per a totes → 1 sol càlcul per frame, sense lerp/parallax).
//     La Y s'embolcalla (modulo) dins la banda de pantalla → scroll infinit continu; el fade de les
//     vores amaga el salt del wrap. NO es connecta res a window: només la posició X/Y/Z de la partícula.
//   · X, Z = deriva lateral/profunditat sinusoïdal (secundari, naturalitat) — a GPU.
//   · Mida i opacitat random per partícula (min/max). Bokeh per profunditat (Z).
//
// camera2D: 1u ≈ 1px a z=0; l'alçada de món de la pantalla ≈ height px → halfH.
// Valors en FRACCIÓ de pantalla (× WIDTH/HEIGHT/ÀREA segons la llei; NONE = tal qual). El comentari
// de cada línia mostra l'equivalent en px a la referència 1440×900 (el que hi havia autorat abans).
export const OXIGEN_SETTINGS = {
  count: 0.000386,        // AREA  · ≈500 @1440×900

  // MIDA (random per partícula)
  sizeMin: 0.000347,      // SCALE · ≈0.5px @1440
  sizeMax: 0.002778,      // SCALE · ≈4px @1440

  // OPACITAT (random per partícula)
  opacityMin: 0.26,       // NONE
  opacityMax: 0.40,       // NONE

  // FLOTACIÓ vertical = pujada (fracció d'alçada/s, random per partícula). MOVIMENT PRINCIPAL.
  floatMin: 0.031111,     // VERT  · ≈10px/s @900
  floatMax: 0.077778,     // VERT  · ≈34px/s @900

  // DERIVA lateral (SECUNDÀRIA, subtil): amplitud X + profunditat Z + velocitat pròpia (≠ vertical).
  driftX: 0.013,         // SCALE · ≈18px @1440
  driftZ: 0.05,           // SCALE · ≈72px @1440
  driftSpeed: 0.97,       // NONE  (freqüència temporal)

  // DISPLACE = lerp de la posició d'scroll. Ritme del lerp PER PARTÍCULA (rang → parallax):
  // ↑ = segueix l'scroll més ràpid (menys lag); ↓ = més lag (queda enrere) → profunditat.
  scrollStrength: 0.32,   // NONE · potència del scroll sobre les bombolles (1 = 1:1; ↓ = es mouen menys)

  speed: .35,             // NONE  (multiplicador global de temps)

  // BOKEH (desenfoc per profunditat / Z)
  focus: 0,               // SCALE · pla de focus (z, px @1440)
  blur: 1.1,             // NONE  (força)

  // COLOR
  color: '#bfe6ff',       // NONE (string hex → colorpicker al pane; Three.Color accepta string igual)

  // VOLUM d'emissió + FORAT central net + marge de fade.
  spreadX: 1.111111,      // SCALE · ≈1600px @1440
  spreadZ: 0.833333,      // SCALE · ≈1200px @1440
  holeX: 0.388889,        // SCALE · ≈560px @1440
  holeY: 0.388889,        // SCALE · ≈560px @1440
  edgeMargin: 0.1,        // VERT  · ≈90px @900
};

const PROJ_Z = 1500.0;
const TAU = 6.2831853;

const VERTEX = /* glsl */`
  uniform float uTime;
  uniform float uSizeMin, uSizeMax;
  uniform float uOpMin, uOpMax;
  uniform float uDriftX, uDriftZ, uDriftSpeed;
  uniform float uFocus, uBlur;
  uniform float uHalfH, uEdgeMargin;
  uniform vec3 uColor;

  attribute vec3 aSeed;    // fases random de la deriva lateral
  attribute vec4 aRand;    // (mida, factor amplitud lateral, opacitat, --) ∈ [0,1]

  varying float vAlpha;
  varying float vCoc;      // circle of confusion (bokeh)
  varying vec3 vColor;

  const float PROJ_Z = ${PROJ_Z.toFixed(1)};
  const float TAU = ${TAU};

  void main() {
    // position.y ja porta flotació + displace de scroll (CPU). Aquí només la deriva lateral X/Z.
    vec3 p = position;
    float af = 0.4 + aRand.y * 1.2;   // amplitud lateral variada per partícula
    p.x += sin((uTime + aSeed.x * 100.0) * uDriftSpeed + aSeed.y * TAU) * uDriftX * af;
    p.z += cos((uTime + aSeed.z * 100.0) * uDriftSpeed * 0.7 + aSeed.x * TAU) * uDriftZ * af;

    vec4 mvPosition = modelViewMatrix * vec4(p, 1.0);
    float viewZ = -mvPosition.z;

    // Bokeh: circle of confusion segons distància al pla de focus (profunditat Z).
    float focusDist = PROJ_Z - uFocus;
    float coc = clamp(abs(viewZ - focusDist) * uBlur * 0.004, 0.0, 3.0);
    vCoc = coc;

    float sz = mix(uSizeMin, uSizeMax, aRand.x);
    gl_PointSize = sz * (PROJ_Z / viewZ) * (1.0 + coc);

    // Fade a les vores (amaga el wrap del scroll infinit) + atenuació fora de focus.
    float m = uEdgeMargin;
    float fadeIn = smoothstep(-uHalfH - m, -uHalfH + m, p.y);
    float fadeOut = 1.0 - smoothstep(uHalfH - m, uHalfH + m, p.y);
    float op = mix(uOpMin, uOpMax, aRand.z);   // OPACITAT random per partícula
    vAlpha = op * (fadeIn * fadeOut) / (1.0 + coc * coc);

    vColor = uColor;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT = /* glsl */`
  precision highp float;

  varying float vAlpha;
  varying float vCoc;
  varying vec3 vColor;

  void main() {
    float d = length(gl_PointCoord - 0.5);
    float edge = 0.12 + vCoc * 0.28;
    float a = smoothstep(0.5, 0.5 - edge, d) * vAlpha;
    if (a <= 0.001) discard;
    gl_FragColor = vec4(vColor, a);
  }
`;

export default class OxigenParticles {
  constructor(opts = {}) {
    if (opts.settings) Object.assign(OXIGEN_SETTINGS, opts.settings);
    this.points = null;
    this.geometry = null;
    this.material = null;
    this._sig = '';
    this._halfH = (typeof window !== 'undefined' ? window.innerHeight : 700) * 0.5;
    this._t = 0;
    // Arrays CPU (Y = flotació; el displace de scroll és comú → no cal array)
    this._pos = null;       // ref a l'atribut position (x,y,z)
    this._floatY = null;    // component de FLOTACIÓ (pujada acumulada) per partícula
    this._floatRand = null; // factor de velocitat de flotació [0,1]
  }

  init(height) {
    if (height) this._halfH = height * 0.5;
    const s = OXIGEN_SETTINGS;
    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uSizeMin: { value: s.sizeMin }, uSizeMax: { value: s.sizeMax },
        uOpMin: { value: s.opacityMin }, uOpMax: { value: s.opacityMax },
        uDriftX: { value: s.driftX }, uDriftZ: { value: s.driftZ }, uDriftSpeed: { value: s.driftSpeed },
        uFocus: { value: s.focus }, uBlur: { value: s.blur },
        uHalfH: { value: this._halfH }, uEdgeMargin: { value: s.edgeMargin },
        uColor: { value: new Color(s.color) },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: NormalBlending,
    });
    this._applyUniforms(); // escala els uniforms dependents de pantalla (× WIDTH/HEIGHT)

    this.geometry = this._buildGeometry();
    this._sig = this._buildSig();
    this.points = new Points(this.geometry, this.material);
    this.points.frustumCulled = false; // el moviment desplaça les partícules fora del bounding box
    return this.points;
  }

  // Emet partícules per TOTA la pantalla (vertical), dins spreadX/spreadZ, excloent el forat central.
  _buildGeometry() {
    const s = OXIGEN_SETTINGS;
    const W = Metrics.WIDTH, H = Metrics.HEIGHT;
    // count = densitat × àrea (fracció per px²), amb sostre de seguretat per GPU.
    const n = Math.min(MAX_COUNT, Math.max(1, Math.floor(W * H * s.count)));
    const pos = new Float32Array(n * 3);
    const seed = new Float32Array(n * 3);
    const rand = new Float32Array(n * 4);
    const floatY = new Float32Array(n);
    const floatRand = new Float32Array(n);

    // Extensions a px: fracció × dimensió de pantalla (holes/spread × WIDTH, marge × HEIGHT).
    const spreadX = W * s.spreadX, spreadZ = W * s.spreadZ;
    const hx = (W * s.holeX) * 0.5, hy = (W * s.holeY) * 0.5;
    const m = H * s.edgeMargin;
    const yLo = -this._halfH - m, yHi = this._halfH + m;

    for (let i = 0; i < n; i++) {
      const y = yLo + Math.random() * (yHi - yLo);
      let x, tries = 0;
      do {
        x = (Math.random() - 0.5) * spreadX;
        tries++;
      } while (Math.abs(x) < hx && Math.abs(y) < hy && tries < 8);

      pos[i * 3] = x;
      pos[i * 3 + 1] = y;
      pos[i * 3 + 2] = (Math.random() - 0.5) * spreadZ;

      floatY[i] = y;          // la flotació arrenca a la posició inicial

      seed[i * 3] = Math.random();
      seed[i * 3 + 1] = Math.random();
      seed[i * 3 + 2] = Math.random();

      rand[i * 4] = Math.random();     // mida
      rand[i * 4 + 1] = Math.random(); // factor amplitud lateral
      rand[i * 4 + 2] = Math.random(); // opacitat
      rand[i * 4 + 3] = Math.random(); // (lliure)

      floatRand[i] = Math.random();
    }

    const geo = new BufferGeometry();
    const posAttr = new Float32BufferAttribute(pos, 3);
    geo.setAttribute('position', posAttr);
    geo.setAttribute('aSeed', new Float32BufferAttribute(seed, 3));
    geo.setAttribute('aRand', new Float32BufferAttribute(rand, 4));

    // IMPORTANT: Float32BufferAttribute COPIA l'array (super(new Float32Array(array))). El buffer que
    // es renderitza és `posAttr.array`, NO el `pos` original → hem d'animar `posAttr.array` perquè la
    // Y (flotació + displace) que calculem al loop arribi realment a la GPU.
    this._pos = posAttr.array;
    this._floatY = floatY;
    this._floatRand = floatRand;
    return geo;
  }

  loop(dt = 0.016) {
    const s = OXIGEN_SETTINGS;
    this._t += dt * s.speed;
    if (this.material) this.material.uniforms.uTime.value = this._t;
    if (!this.points) return;

    const scrollY = Scroll.y || 0;

    const H = Metrics.HEIGHT;
    const halfH = this._halfH;
    const m = H * s.edgeMargin;     // marge vertical: fracció × alçada
    const bandLo = -halfH - m;      // vora inferior de la banda (fora de pantalla)
    const band = (halfH + m) * 2;   // alçada total de la banda

    const pos = this._pos;
    const floatY = this._floatY;
    const n = floatY.length;
    // Velocitat de pujada en px/s = fracció × alçada (temps de creuament constant a qualsevol pantalla).
    const floatMin = H * s.floatMin;
    const floatSpan = H * (s.floatMax - s.floatMin);
    // DISPLACE de scroll: directe (sense lerp/parallax per partícula) → IGUAL per a totes → 1 sol càlcul.
    const dispV = scrollY * s.scrollStrength;

    for (let i = 0; i < n; i++) {
      // FLOTACIÓ: pujada acumulada (velocitat random per partícula) + displace de scroll (comú).
      floatY[i] += (floatMin + floatSpan * this._floatRand[i]) * dt * s.speed;
      // Y = flotació + displace, embolcallada dins la banda (scroll infinit; el fade amaga el wrap).
      let y = floatY[i] + dispV;
      y = ((y - bandLo) % band + band) % band + bandLo;
      pos[i * 3 + 1] = y;
    }

    this.geometry.attributes.position.needsUpdate = true;
  }

  // Aplica als uniforms els valors dependents de pantalla (fracció × Metrics.WIDTH/HEIGHT) + els NONE.
  _applyUniforms() {
    if (!this.material) return;
    const u = this.material.uniforms;
    const s = OXIGEN_SETTINGS;
    const W = Metrics.WIDTH, H = Metrics.HEIGHT;
    u.uSizeMin.value = W * s.sizeMin; u.uSizeMax.value = W * s.sizeMax;   // SCALE
    u.uDriftX.value = W * s.driftX; u.uDriftZ.value = W * s.driftZ;        // SCALE
    u.uFocus.value = W * s.focus;                                          // SCALE
    u.uEdgeMargin.value = H * s.edgeMargin;                                // VERT
    u.uOpMin.value = s.opacityMin; u.uOpMax.value = s.opacityMax;          // NONE
    u.uDriftSpeed.value = s.driftSpeed; u.uBlur.value = s.blur;            // NONE
    u.uColor.value.set(s.color);                                          // NONE
  }

  // La geometria depèn de count/spread/hole (fracció) I de la mida de pantalla → incloem W/H a la
  // signatura perquè el resize dispari el rebuild del volum d'emissió.
  _buildSig() {
    const s = OXIGEN_SETTINGS;
    return [s.count, s.spreadX, s.spreadZ, s.holeX, s.holeY, Metrics.WIDTH, Metrics.HEIGHT].join(',');
  }

  setConfig(partial = {}) {
    Object.assign(OXIGEN_SETTINGS, partial);
    this._applyUniforms();
    this._rebuildIfNeeded();
  }

  resize(height) {
    if (height) this._halfH = height * 0.5;
    if (this.material) this.material.uniforms.uHalfH.value = this._halfH;
    this._applyUniforms();      // W/H han canviat → reescala els uniforms
    this._rebuildIfNeeded();    // la sig inclou W/H → reconstrueix el volum d'emissió
  }

  // Reconstrueix la geometria si la signatura (settings de volum + mida de pantalla) ha canviat.
  _rebuildIfNeeded() {
    const sig = this._buildSig();
    if (this.points && sig !== this._sig) {
      this.geometry.dispose();
      this.geometry = this._buildGeometry();
      this.points.geometry = this.geometry;
      this._sig = sig;
    }
  }

  setLayer(n) { if (this.points) this.points.layers.set(n); }

  dispose() {
    this.geometry?.dispose();
    this.material?.dispose();
    this.geometry = null;
    this.material = null;
    this.points = null;
    this._pos = this._floatY = this._floatRand = null;
  }

  get settings() { return OXIGEN_SETTINGS; }
}

// Exportat per si es vol un look de glow additiu en comptes de bombolles toves.
export { AdditiveBlending };
